import React, { useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { FormControlLabel, Switch, Button } from "@mui/material";
import { GridColumnIcon } from "@mui/x-data-grid";
import { makeStyles } from "tss-react/mui";
import "../button/Buttons.js";
import "../button/EditButton.js";
import "@mui/material/Popover";
import { LocalizedText } from "../i18n/LocalizedText.js";
import "../../localization/MessageLocalizer.js";
import "../../localization/LocaleProvider.js";
import "../../localization/sharedResources.js";
import "../dropdown/DropdownHoc.style.js";
import { DropdownWithChip } from "../dropdown/DropdownWithChip.js";
import "../dropdown/DropdownItem.js";
import { Icon } from "../icons/Icon.js";
import { CustomIcon } from "../icons/types.js";
import "../icons/ExpandMoreIcon.js";
import "../input/validation.js";
import "../input/DatePicker.js";
import "../input/DateRangePicker.js";
import "../input/StyledInput.js";
import { FormTextInputCleanable } from "../input/FormInput.js";
import "../input/StyledFormInputHoc.js";
import "moment-timezone";
import "../input/DateSinglePicker.localize.js";
import "../input/hooks.js";
const localized = defineMessages({
  searchInputPlaceholder: {
    id: "DataGridColumnsPanel_searchInputPlaceholder",
    defaultMessage: "Find column"
  },
  hideAllButtonLabel: {
    id: "DataGridColumnsPanel_hideAllButtonLabel",
    defaultMessage: "Hide all"
  },
  showAllButtonLabel: {
    id: "DataGridColumnsPanel_showAllButtonLabel",
    defaultMessage: "Show all"
  },
  openPanel: {
    id: "DataGridColumnsPanel_openPanel",
    defaultMessage: "Columns"
  }
});
const useStyles = makeStyles()((theme) => ({
  gridColumnsPanelWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: "1",
    height: "max-content",
    maxHeight: "550px",
    backgroundColor: theme.custom.palette.content.backgroundWhite,
    borderRadius: theme.custom.defaultBorderRadius * 2
  },
  gridColumnsPanelHeader: {
    padding: theme.spacing(1.5),
    "& .MuiFormControl-root": {
      margin: 0
    }
  },
  gridColumnsPanelSearchInputIcon: {
    fontSize: theme.custom.inputIconFontSize,
    color: theme.palette.secondary.main
  },
  gridColumnsPanelContent: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    flex: "1 1",
    maxHeight: "unset",
    gap: "16px",
    padding: theme.spacing(0, 1.5, 1)
  },
  gridColumnsPanelColumn: {
    marginLeft: theme.spacing(1.5),
    "& .MuiSwitch-root": {
      marginRight: theme.spacing(1)
    }
  },
  gridColumnsPanelFooter: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1.75, 1.5),
    borderTop: `1px solid ${theme.palette.secondary.light}`,
    "& .MuiButton-text": {
      padding: 0,
      minWidth: "unset"
    }
  },
  gridColumnButton: {
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(2)
    },
    marginBottom: theme.spacing(2),
    "& .MuiButtonBase-root": {
      backgroundColor: theme.custom.palette.content.backgroundWhite,
      fontSize: theme.typography.body1.fontSize,
      padding: theme.spacing(2),
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.secondary.light}`,
      boxShadow: `0px 1px 1px ${theme.custom.palette.content.boxShadow}`,
      "& .MuiButton-startIcon": {
        color: theme.palette.secondary.main
      },
      "&:hover": {
        color: theme.palette.primary.main,
        "& .MuiButton-startIcon": {
          color: theme.palette.primary.main
        }
      }
    },
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(2)
    }
  }
}));
function DataGridColumnsPanel({
  pinnedColumnNames,
  visibleColumns,
  onColumnsVisibilityUpdate,
  allColumns
}) {
  const { classes } = useStyles();
  const [searchValue, setSearchValue] = useState("");
  const { formatMessage } = useIntl();
  const columns = allColumns?.filter((c) => !pinnedColumnNames?.includes(c.field));
  const searchedColumns = React.useMemo(() => {
    let result = columns;
    if (searchValue) {
      result = columns.filter((column) => formatMessage(column.title).toLowerCase().includes(searchValue.toLowerCase()));
    }
    return result;
  }, [columns?.map((c) => c.field)?.join(), searchValue]);
  function handleSearchValueChange(event) {
    setSearchValue(event.target.value);
  }
  function handleColumnClick(event) {
    const name = event.target.name;
    const column = allColumns.find((c) => c.field === name);
    const newColumns = !visibleColumns.includes(column.field) ? showColumns([name]) : hideColumns([name]);
    onColumnsVisibilityUpdate(newColumns);
  }
  function handleToggleAllColumns(value) {
    const searchedColumnNames = searchedColumns?.map((c) => c.field);
    const newColumns = value ? hideColumns(searchedColumnNames) : showColumns([...searchedColumnNames]);
    onColumnsVisibilityUpdate(newColumns);
  }
  function hideColumns(names) {
    return allColumns.filter((c) => visibleColumns.includes(c.field) && !names.includes(c.field)).map((c) => c.field);
  }
  function showColumns(names) {
    return [.../* @__PURE__ */ new Set([...allColumns.filter((c) => visibleColumns.includes(c.field)).map((c) => c.field), ...names])];
  }
  function handleClearSearch() {
    setSearchValue("");
  }
  return /* @__PURE__ */ React.createElement(
    DropdownWithChip,
    {
      className: classes.gridColumnButton,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      selectedText: localized.openPanel,
      startIcon: /* @__PURE__ */ React.createElement(GridColumnIcon, { fontSize: "small" }),
      hasArrow: false,
      isMobileFullWidth: false
    },
    /* @__PURE__ */ React.createElement("div", { className: classes.gridColumnsPanelWrapper }, /* @__PURE__ */ React.createElement("div", { className: classes.gridColumnsPanelHeader }, /* @__PURE__ */ React.createElement(
      FormTextInputCleanable,
      {
        startAdornment: /* @__PURE__ */ React.createElement(Icon, { icon: CustomIcon.Search, className: classes.gridColumnsPanelSearchInputIcon }),
        placeholder: localized.searchInputPlaceholder,
        value: searchValue,
        onChange: handleSearchValueChange,
        clear: handleClearSearch
      }
    )), /* @__PURE__ */ React.createElement("div", { className: classes.gridColumnsPanelContent }, searchedColumns.map((column) => /* @__PURE__ */ React.createElement("div", { key: column.field, className: classes.gridColumnsPanelColumn }, /* @__PURE__ */ React.createElement(
      FormControlLabel,
      {
        control: /* @__PURE__ */ React.createElement(
          Switch,
          {
            checked: visibleColumns.includes(column.field),
            onClick: handleColumnClick,
            name: column.field,
            color: "secondary"
          }
        ),
        label: formatMessage(column.title) || column.field
      }
    )))), /* @__PURE__ */ React.createElement("div", { className: classes.gridColumnsPanelFooter }, /* @__PURE__ */ React.createElement(Button, { onClick: () => handleToggleAllColumns(true), color: "primary" }, /* @__PURE__ */ React.createElement(LocalizedText, { label: localized.hideAllButtonLabel })), /* @__PURE__ */ React.createElement(Button, { onClick: () => handleToggleAllColumns(false), color: "primary" }, /* @__PURE__ */ React.createElement(LocalizedText, { label: localized.showAllButtonLabel }))))
  );
}
export {
  DataGridColumnsPanel
};
//# sourceMappingURL=DataGridColumnsPanel.js.map
